import {
  type UseMutationOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { createProperty } from "api/properties";

import { Property } from "js/components/types";

import { queryKeys } from "./queryKeys";

export const useCreateProperty = (
  options?: UseMutationOptions<Property, Error, Property>,
) => {
  const queryClient = useQueryClient();
  const { onSuccess, onError, onSettled, ...restOptions } = options ?? {};

  return useMutation<Property, Error, Property>({
    mutationFn: async (property: Property): Promise<Property> => {
      return await createProperty(property);
    },
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(
        queryKeys.properties.list(undefined),
        (oldData: Property[] | undefined) => {
          if (!oldData) return [data];
          return [data, ...oldData];
        },
      );

      queryClient.invalidateQueries({ queryKey: queryKeys.properties.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.properties.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.properties.organization(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.properties.usages(),
      });

      onSuccess?.(data, variables, context);
    },
    onError: (error: Error, variables, context) => {
      console.error("Error creating property:", error);
      onError?.(error, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      onSettled?.(data, error, variables, context);
    },
    ...restOptions,
  });
};
